import { Component, AfterViewInit } from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { LanguageService } from './core/language.service';
import { CmsService } from './core/cms.service';
import { AnalyticsService } from './core/analytics.service';
import { SeoOverride } from './core/cms.models';

declare var jQuery: any;
declare var cursurCircle: any;
declare var initializeOnReady: any;
declare var initializeOnLoad: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements AfterViewInit {
  title = 'New Maven Merge';

  /** Per-route browser-tab titles, keyed by URL path (no leading slash, no query). */
  private readonly pageTitles: Record<string, string> = {
    '': 'Home',
    'index': 'Home',
    'index2': 'Home',
    'index3': 'Home',
    'company/about': 'About Us',
    'company/faq': 'FAQ',
    'company/gallery': 'Gallery',
    'company/testimonials': 'Testimonials',
    'company/why-choose-us': 'Why Choose Us',
    'company/pricing': 'Pricing',
    'company/team': 'Our Team',
    'company/contact': 'Contact Us',
    'company/careers': 'Careers',
    'company/careers/detail': 'Job Details',
    'company/offers': 'Offers',
    'itsolutions/mission': 'Mission & Vision',
    'itsolutions/services': 'Services',
    'itsolutions/services/detail': 'Service Details',
    'itsolutions/portfolio/1': 'Our Work',
    'itsolutions/portfolio/2': 'Our Work',
    'itsolutions/portfolio/detail': 'Case Study',
    'itblogs/blogs/grid': 'News & Blog',
    'itblogs/blogs/detail': 'Article'
  };

  /** Admin-managed per-page SEO overrides, keyed by normalized route path. */
  private seoMap: Record<string, SeoOverride> = {};
  private currentPath = '';

  constructor(private router: Router, public lang: LanguageService, private titleSvc: Title,
              private meta: Meta, private cms: CmsService, private analytics: AnalyticsService) {}

  ngAfterViewInit(): void {
    // Load SEO overrides once; re-apply to the current page when they arrive.
    this.cms.getSeoOverrides().subscribe({
      next: (list) => {
        for (const o of list || []) this.seoMap[o.path] = o;
        this.applySeoOverride(this.currentPath);
      },
      error: () => { /* fall back to defaults if overrides can't load */ }
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.setPageTitle(event.urlAfterRedirects);
        this.reinitializeThings();
      }
    });

    // Re-init the theme (carousels rebuild as LTR/RTL) when the language toggles, so we
    // get correct RTL WITHOUT a full page reload (a reload hard-GETs the deep route and
    // blanks the page if the host's SPA fallback isn't serving index.html).
    let first = true;
    this.lang.lang$.subscribe(() => {
      if (first) { first = false; return; }
      this.reinitializeThings();
    });
  }

  /** Sets the document title + og:title from the route map, with a brand suffix; defaults for unknown URLs. */
  private setPageTitle(url: string): void {
    const path = (url || '').split('?')[0].split('#')[0].replace(/^\//, '');
    this.currentPath = path;
    const name = this.pageTitles[path];
    const fullTitle = name ? `${name} | Maven Merge` : 'Maven Merge | ERP & Digital Transformation Solutions';
    this.titleSvc.setTitle(fullTitle);
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    // Admin override (if any) wins over the default.
    this.applySeoOverride(path);
    // Record the view (only sent if the visitor granted cookie consent).
    this.analytics.trackPageView('/' + path);
  }

  /** Applies an admin SEO override for the given path on top of the defaults. */
  private applySeoOverride(path: string): void {
    // Home renders at index/index2/index3 — map those to the '' override key.
    const key = (path === 'index' || path === 'index2' || path === 'index3') ? '' : path;
    const o = this.seoMap[key];
    if (!o) return;

    const ar = this.lang.isArabic;
    const title = (ar ? (o.titleAr || o.titleEn) : (o.titleEn || o.titleAr)) || '';
    const desc = (ar ? (o.descriptionAr || o.descriptionEn) : (o.descriptionEn || o.descriptionAr)) || '';

    if (title) {
      const full = `${title} | Maven Merge`;
      this.titleSvc.setTitle(full);
      this.meta.updateTag({ property: 'og:title', content: full });
    }
    if (desc) {
      this.meta.updateTag({ name: 'description', content: desc });
      this.meta.updateTag({ property: 'og:description', content: desc });
    }
    if (o.ogImageUrl) {
      this.meta.updateTag({ property: 'og:image', content: this.cms.mediaUrl(o.ogImageUrl) });
    }
  }

  private reinitializeThings() {
    // The theme initializes owl carousels/sliders ONCE, before our async API data has
    // rendered (much worse on a cold-started API in prod) — leaving them empty (blank
    // page until a manual refresh). Re-calling owl init on an already-initialized empty
    // carousel is a no-op, so we DESTROY then re-init, and do it across a few passes so
    // late-arriving data still gets picked up.
    const reinit = () => {
      try {
        // The theme inits owl carousels without an `rtl` option, so in Arabic the slides
        // are transformed off-screen and the section looks empty. Set owl's GLOBAL default
        // to match the current direction before (re)initializing.
        const rtl = document.documentElement.getAttribute('dir') === 'rtl';
        if (jQuery.fn && jQuery.fn.owlCarousel && jQuery.fn.owlCarousel.Constructor) {
          jQuery.fn.owlCarousel.Constructor.Defaults.rtl = rtl;
        }
        jQuery('.owl-carousel.owl-loaded').trigger('destroy.owl.carousel');
      } catch (e) { /* owl not ready */ }
      try { initializeOnReady(); initializeOnLoad(); } catch (e) { /* theme fns not ready yet */ }
    };
    setTimeout(reinit, 1200);
    setTimeout(reinit, 3000);
    setTimeout(reinit, 6000);  // safety pass for a slow (cold-start) API
  }
}
